// Self-diagnosing fetch-loop circuit breaker.
//
// Installed from index.js so it runs for every fetch in the app. If the
// same URL fires more than FUSE_MAX times in FUSE_WINDOW_MS, we stop
// forwarding requests to it and log a single loud error naming the
// endpoint. That turns a silent runaway useEffect (which would otherwise
// just spam the server and freeze the UI by starving the event loop)
// into a one-line message we can act on.
//
// On every trip the breaker also captures a stack trace of where the
// offending fetch was called from — paste it into the bug report and
// we can usually pinpoint the rogue effect in a minute.
//
// This is a diagnostic belt, not a feature. Remove the install call
// once the dashboard has gone a few sessions without tripping it.

const FUSE_MAX = 30;
const FUSE_WINDOW_MS = 5000;
const TRIP_LOG_DEBOUNCE_MS = 10_000;

export function installFetchLoopGuard() {
  if (typeof window === 'undefined' || window.__ppFetchGuardInstalled) return;
  window.__ppFetchGuardInstalled = true;

  const history = new Map();   // normalised URL → array of timestamps
  const tripped = new Map();   // normalised URL → lastLoggedAt
  const origFetch = window.fetch.bind(window);

  window.fetch = function (input, init) {
    const url = typeof input === 'string' ? input : (input && input.url) || '';
    const key = url.split('?')[0] || url;
    const now = Date.now();
    const stamps = (history.get(key) || []).filter(t => now - t < FUSE_WINDOW_MS);
    stamps.push(now);
    history.set(key, stamps);

    if (stamps.length > FUSE_MAX) {
      const lastLogged = tripped.get(key) || 0;
      if (now - lastLogged > TRIP_LOG_DEBOUNCE_MS) {
        tripped.set(key, now);
        // Capture where this fetch was called from. The topmost non-guard
        // frame points at the useEffect or handler that's looping.
        const stack = new Error('fetch-loop trace').stack || '';
        // eslint-disable-next-line no-console
        console.error(
          `[pp-loop-guard] ${stamps.length}× ${key} in ${FUSE_WINDOW_MS / 1000}s — loop detected, blocking further calls.\n` +
          `Paste this full message to the developer. First caller frame:\n` +
          stack.split('\n').slice(2, 6).join('\n')
        );
      }
      return Promise.reject(
        new Error(`pp-loop-guard: too many requests to ${key} in ${FUSE_WINDOW_MS / 1000}s`)
      );
    }

    return origFetch(input, init);
  };
}
